import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Badge,
  Alert,
  Spinner,
  ListGroup,
  ListGroupItem
} from 'reactstrap';
import {
  FaPlus,
  FaMinus,
  FaSave,
  FaTimes,
  FaUser,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaFileInvoice,
  FaTrash,
  FaEdit,
  FaSearch,
  FaBarcode,
  FaCalculator,
  FaTruck,
  FaInfoCircle,
  FaCheck,
  FaCheckCircle,
  FaMoneyBill,
  FaFileAlt,
  FaUniversity,
  FaCreditCard,
  FaFileContract,
  FaQuestion
} from 'react-icons/fa';

import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import Select from 'react-select/async';
import { components } from 'react-select';
import { debounce } from 'lodash';

// Redux actions
import { productActions } from '../../sagas/productSlice';
import { companyActions } from '../../sagas/companySlice';

// Utilities
import { formatPrice } from '../../helpers/utility';
import { useLocation } from 'react-router-dom';
import { invoiceSupplierActions } from '../../sagas/invoiceSupplierSlice';
import { reglementsFournisseurActions } from '../../sagas/reglementFournisseurSlice';
import { supplierActions } from '../../sagas/supplierSlice';

// Validation schema
const validationSchema = Yup.object({
  numero: Yup.string().required('Numéro de facture requis'),
  dateFacture: Yup.date().required('Date de facture requise'),
  fournisseurId: Yup.number().required('Client requis'),
  companyId: Yup.number().required('Entreprise requise'),
  modeReglement: Yup.string()
    .required('Mode de règlement requis')
    .oneOf(['ESPECES', 'CHEQUE', 'VIREMENT', 'TRAITE', 'CARTE_BANCAIRE'], 'Mode de règlement invalide'),

  // Validation conditionnelle corrigée pour numeroCheque
  numeroCheque: Yup.string().when('modeReglement', {
    is: 'CHEQUE',
    then: (schema) => schema.required('Numéro de chèque requis'),
    otherwise: (schema) => schema.notRequired()
  }),

  // Validation conditionnelle corrigée pour banque
  banque: Yup.string().when('modeReglement', {
    is: 'CHEQUE',
    then: (schema) => schema.required('Nom de la banque requis'),
    otherwise: (schema) => schema.notRequired()
  }),

  

  lignes: Yup.array()
    .min(1, 'Au moins un article est requis')
    .of(
      Yup.object({
        articleId: Yup.number().required(),
        quantite: Yup.number().min(0.01, 'Quantité doit être supérieure à 0').required(),
        prixUnitaireHT: Yup.number().min(0, 'Prix unitaire doit être positif').required(),
        tauxTVA: Yup.number().min(0, 'Taux TVA doit être positif').max(1, 'Taux TVA ne peut pas dépasser 100%')
      })
    )
});

const ViewFacture = ({ invoice = null, initialValues: suppliedInitialValues = null, onSave, onCancel, isEditing = false, fromPayments = false, selectedPayments = [] }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // Redux selectors
  const { loading: invoiceLoading } = useSelector(state => state.invoices);
  const { loadingPayment: reglementsLoading } = useSelector(state => state.invoiceSupplier);
  const { products, loading: productsLoading } = useSelector(state => state.products);
  const { clients } = useSelector(state => state.clients);
  const unites = useSelector(state => state.products.unites);
  const { companies } = useSelector(state => state.companies);

  const canBeUpdated = !isEditing || invoice?.statut === 'BROUILLON';
  // Local state
  const [productOptions, setProductOptions] = useState([]);
  const [showClientModal, setShowClientModal] = useState(false);
  const [showProductSearch, setShowProductSearch] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [invoicePayments, setInvoicePayments] = useState([]);
  const [showSelectPaymentsModal, setShowSelectPaymentsModal] = useState(false);
  const [availablePayments, setAvailablePayments] = useState([]);
  const [selectedPaymentsForInvoice, setSelectedPaymentsForInvoice] = useState(selectedPayments || []);
  const [paymentSearchTerm, setPaymentSearchTerm] = useState('');
  const [clientForPayments, setClientForPayments] = useState(null);
  const [activeStep, setActiveStep] = useState(1);

  // Refs
  const productSearchRef = useRef(null);

  const location = useLocation();

  // Si les props ne sont pas passées directement, essayez de les récupérer de location.state
  const stateFromPayments = location.state?.fromPayments || fromPayments;
  const stateSelectedPayments = location.state?.selectedPayments || selectedPayments;
  const stateFournisseurId = location.state?.fournisseurId;
  const invoiceFromPayments = Boolean(stateFromPayments || invoice?.fromPayments);
  const invoicePaymentsRefs = invoice?.paymentsRefs || stateSelectedPayments.map(payment => payment.reference || payment.id).join(', ');

  // Ajoutez ces états
  const [paymentsForInvoice, setPaymentsForInvoice] = useState(stateSelectedPayments || []);
  const [showPaymentsDetails, setShowPaymentsDetails] = useState(invoiceFromPayments);

  // Calculer le total des paiements
  const calculatePaymentsTotal = useCallback(() => {
    return paymentsForInvoice.reduce((sum, payment) => sum + (payment.montantRegle || 0), 0);
  }, [paymentsForInvoice]);


  // Initial values for formik
  const getInitialValues = () => {
    if (isEditing && invoice) {
      return {
        numero: invoice.numero || '',
        numeroFactfournisseur: invoice.numeroFactfournisseur || '',
        dateFacture: invoice.dateFacture || new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Casablanca' }),
        dateEcheance: invoice.dateEcheance || '',
        fournisseurId: invoice.fournisseurId || null,
        companyId: invoice.companyId || invoice.company?.id || null,
        fournisseurNom: invoice.fournisseurNom || '',
        modeReglement: invoice.modeReglement,
        numeroCheque: invoice.numeroCheque || '',
        dateEcheanceCheque: invoice.dateEcheanceCheque,
        banque: invoice.banque || '',
        commentaire: invoice.commentaire || '',
        lignes: invoice.lignes || [],
        montantHT: invoice.montantHT || 0,
        montantTTC: invoice.montantTTC || 0,
        montantTVA: invoice.montantTVA || 0,
        montantRegle: invoice.fromPayments ? (invoice.montantTTC || 0) : (invoice.montantRegle || 0),
        statut: invoice.fromPayments ? 'PAYEE' : (invoice.statut || 'VALIDEE'),
        fromPayments: Boolean(invoice.fromPayments),
        paymentsRefs: invoice.paymentsRefs || ''
      };
    }

    // Pour une nouvelle facture
    const defaults = {
      numero: '',
      numeroFactfournisseur: '',
      dateFacture: new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Casablanca' }),
      dateEcheance: '',
      fournisseurId: stateFournisseurId || null,
      companyId: null,
      fournisseurNom: '',
      modeReglement: null,
      numeroCheque: '',
      dateEcheanceCheque: null,
      banque: '',
      commentaire: '',
      lignes: [],
      montantHT: 0,
      montantTTC: 0,
      montantTVA: 0,
      montantRegle: invoiceFromPayments ? calculatePaymentsTotal() : 0,
      statut: invoiceFromPayments ? 'PAYEE' : 'BROUILLON',
      fromPayments: invoiceFromPayments,
      paymentsRefs: stateSelectedPayments.map(payment => payment.reference || payment.id).join(', ')
    };
    const source = suppliedInitialValues || {};
    return {
      ...defaults,
      ...source,
      lignes: Array.isArray(source.lignes) ? source.lignes : defaults.lignes,
      companyId: source.companyId || defaults.companyId,
      fournisseurId: source.fournisseurId || defaults.fournisseurId,
      referenceBons: source.referenceBons || (source.receiptMappings || []).map(receipt => receipt.numero).join(','),
    };
  };

  const getPaymentModeIcon = (mode) => {
    switch (mode) {
      case 'ESPECES':
        return <FaMoneyBill className="text-success" />;
      case 'CHEQUE':
        return <FaFileAlt className="text-primary" />;
      case 'VIREMENT':
        return <FaUniversity className="text-info" />;
      case 'CARTE_BANCAIRE':
        return <FaCreditCard className="text-dark" />;
      default:
        return <FaQuestion className="text-secondary" />;
    }
  };


  // Formik configuration
  const formik = useFormik({
    initialValues: getInitialValues(),
    validationSchema,
    onSubmit: (values) => {
      handleSaveInvoice(values);
    }
  });

  useEffect(() => {
    if (!companies || companies.length === 0) {
      dispatch(companyActions.getAll({}));
    }
  }, [companies, dispatch]);

  // Load all payments for a client
  const loadClientPayments = (fournisseurId) => {
    if (!fournisseurId) return;

    dispatch(reglementsFournisseurActions.getReglementsByFournisseurRequest({
      fournisseurId,
      page: 0,
      size: 100,
      onSuccess: (data) => {
        // Filter to only include payments that don't have an associated invoice
        const availableForInvoice = data.content.filter(payment =>
          !payment.allocations ||
          !payment.allocations.some(a => a.documentType === 'FACTURE')
        );
        setAvailablePayments(availableForInvoice);
      },
      onError: (error) => {
        console.error('Error loading client payments:', error);
        setAvailablePayments([]);
      }
    }));
  };


  useEffect(() => {
    if (isEditing && invoice?.numero) {
      loadInvoicePayments(invoice.numero);
    }
  }, [isEditing, invoice?.numero]);

  // Load invoice payments
  const loadInvoicePayments = (numero) => {
    if (!numero) return;

    dispatch(invoiceSupplierActions.fetchInvoiceReglements({
      numero,
      onSuccess: (data) => {
        setInvoicePayments(data || []);
      },
      onError: (error) => {
        console.error('Error loading payments:', error);
        setInvoicePayments([]);
      }
    }));
  };

  // Load initial data
  useEffect(() => {
    dispatch(productActions.findAllUnites());
  }, []);


  const parseBonLivraisonRefs = (refs) => {
    if (!refs || refs.trim() === '') return [];
    return refs.split(',').map(ref => ref.trim()).filter(ref => ref !== '');
  };

  // Handle save invoice
  const handleSaveInvoice = (values) => {
    if (values.lignes.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: t('facture.noLines'),
        text: t('facture.noLinesMessage')
      });
      return;
    }

    const invoiceData = {
      ...values,
      companyId: values.companyId ? Number(values.companyId) : null,
      fromPayments: Boolean(values.fromPayments),
      paymentsRefs: values.paymentsRefs || invoicePaymentsRefs,
      montantRegle: values.fromPayments ? parseFloat(values.montantTTC || 0) : parseFloat(values.montantRegle || 0),
      statut: values.fromPayments ? 'PAYEE' : values.statut,
      lignes: values.lignes.map(ligne => ({
        ...ligne,
        montantHT: parseFloat(ligne.montantHT || 0),
        montantTTC: parseFloat(ligne.montantTTC || 0),
        montantTaxe: parseFloat(ligne.montantTaxe || 0),
        quantite: parseFloat(ligne.quantite || 0),
        prixUnitaireHT: parseFloat(ligne.prixUnitaireHT || 0),
        prixUnitaireTTC: parseFloat(ligne.prixUnitaireTTC || 0),
        tauxTVA: parseFloat(ligne.tauxTVA || 0)
      }))
    };

    if (isEditing && invoice?.id) {
      console.log(invoice)
      // Mise à jour d'une facture existante
      dispatch(invoiceSupplierActions.updateInvoiceRequest({
        id: invoice.id,
        invoiceData,
        onSuccess: () => {
          Swal.fire({
            icon: 'success',
            title: t('facture.updateSuccess'),
            text: t('facture.updateSuccessMessage'),
            timer: 2000,
            showConfirmButton: false
          });
          if (onSave) {
            onSave();
          }
        },
        onError: (error) => {
          Swal.fire({
            icon: 'error',
            title: t('facture.updateError'),
            text: error.message || t('facture.updateErrorMessage'),
          });
        }
      }));
    } else {
      // Création d'une nouvelle facture
      dispatch(invoiceSupplierActions.createInvoice({
        invoice: invoiceData,
        onSuccess: (newInvoice) => {
          Swal.fire({
            icon: 'success',
            title: t('facture.createSuccess'),
            text: t('facture.createSuccessMessage'),
            timer: 2000,
            showConfirmButton: false
          });
          if (onSave) {
            onSave(newInvoice);
          }
        },
        onError: (error) => {
          Swal.fire({
            icon: 'error',
            title: t('facture.createError'),
            text: error.message || t('facture.createErrorMessage'),
          });
        }
      }));
    }
  };

  // Handle cancel invoice
  const handleCancelInvoice = () => {
    if (!isEditing || !invoice?.id) return;

    Swal.fire({
      title: t('facture.confirmCancel'),
      text: t('facture.confirmCancelMessage'),
      icon: 'warning',
      input: 'textarea',
      inputLabel: t('facture.cancelReason'),
      inputPlaceholder: t('facture.enterCancelReason'),
      inputValidator: (value) => {
        if (!value || value.trim().length === 0) {
          return t('facture.cancelReasonRequired');
        }
      },
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: t('facture.confirmCancelButton'),
      cancelButtonText: t('common.cancel')
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(invoiceSupplierActions.cancelInvoiceRequest({
          id: invoice.id,
          motifAnnulation: result.value,
          onSuccess: () => {
            Swal.fire({
              icon: 'success',
              title: t('facture.cancelSuccess'),
              text: t('facture.cancelSuccessMessage'),
              timer: 2000,
              showConfirmButton: false
            });
            if (onCancel) {
              onCancel();
            }
          },
          onError: (error) => {
            Swal.fire({
              icon: 'error',
              title: t('facture.cancelError'),
              text: error.message || t('facture.cancelErrorMessage'),
            });
          }
        }));
      }
    });
  };

  // Get unit label
  const getUniteLabel = (uniteCode) => {
    const unite = unites.find(u => u.code === uniteCode);
    return unite ? unite.libelle : uniteCode;
  };


  // Get payment mode label
  const getPaymentModeLabel = (mode) => {
    switch (mode) {
      case 'ESPECES': return t('facture.modeReglement.espece');
      case 'CHEQUE': return t('facture.modeReglement.cheque');
      case 'VIREMENT': return t('facture.modeReglement.virement');
      case 'CARTE_BANCAIRE': return t('facture.modeReglement.card');
      default: return mode;
    }
  };

  // Get payment status label
  const getPaymentStatusLabel = (status) => {
    switch (status) {
      case 'VALIDE': return t('payment.validated');
      case 'EN_ATTENTE': return t('payment.pending');
      case 'ANNULE': return t('payment.cancelled');
      case 'ENCAISSE': return t('payment.cashed');
      case 'REJETE': return t('payment.rejected');
      default: return status;
    }
  };

  // Ajoutez cette fonction pour obtenir le badge du statut
  const getPaymentStatusBadge = (statut) => {
    const colors = {
      'EN_ATTENTE': 'warning',
      'VALIDE': 'info',
      'ENCAISSE': 'success',
      'REJETE': 'danger',
      'ANNULE': 'secondary'
    };

    return (
      <Badge color={colors[statut] || 'secondary'}>
        {t(`reglement.${statut.toLowerCase()}`)}
      </Badge>
    );
  };

  // Get payment status color
  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'VALIDE': return 'success';
      case 'EN_ATTENTE': return 'warning';
      case 'ANNULE': return 'danger';
      case 'ENCAISSE': return 'primary';
      case 'REJETE': return 'danger';
      default: return 'secondary';
    }
  };

  // Calculate total paid amount
  const calculateTotalPaid = () => {
    return invoicePayments.reduce((sum, payment) => sum + (payment.montant ?? payment.montantRegle ?? 0), 0);
  };

  // Calculate remaining amount to pay
  const calculateRemainingAmount = () => {
    const totalPaid = calculateTotalPaid();
    return totalPaid > formik.values.montantTTC ? 0 : formik.values.montantTTC - totalPaid;
  };

  const updateInvoiceLineQuantity = (index, rawValue) => {
    const lines = [...formik.values.lignes];
    const line = { ...lines[index] };
    const maximum = Number(line.quantiteRestanteFacturable ?? line.quantiteRecue ?? line.quantite ?? 0);
    const quantity = Math.max(0, Math.min(Number(rawValue || 0), maximum));
    line.quantite = quantity;
    line.montantHT = quantity * Number(line.prixUnitaireHT || 0);
    line.montantTTC = quantity * Number(line.prixUnitaireTTC || 0);
    line.montantTVA = line.montantTTC - line.montantHT;
    lines[index] = line;
    formik.setFieldValue('lignes', lines);
    formik.setFieldValue('montantHT', lines.reduce((sum, item) => sum + Number(item.montantHT || 0), 0));
    formik.setFieldValue('montantTVA', lines.reduce((sum, item) => sum + Number(item.montantTVA || 0), 0));
    formik.setFieldValue('montantTTC', lines.reduce((sum, item) => sum + Number(item.montantTTC || 0), 0));
  };

  // Ajoutez cette fonction pour supprimer un paiement de la liste
  const handleRemovePayment = (paymentId) => {
    setPaymentsForInvoice(paymentsForInvoice.filter(p => p.id !== paymentId));

    // Recalculer le montant réglé
    const newTotal = paymentsForInvoice.filter(p => p.id !== paymentId)
      .reduce((sum, p) => sum + (p.montantRegle || 0), 0);

    formik.setFieldValue('montantRegle', newTotal);

    // Mettre à jour le statut si nécessaire
    if (newTotal >= formik.values.montantTTC) {
      formik.setFieldValue('statut', 'PAYEE');
    } else if (newTotal > 0) {
      formik.setFieldValue('statut', 'PARTIELLEMENT_PAYEE');
    } else {
      formik.setFieldValue('statut', 'BROUILLON');
    }
  };


  // Calculate total amount from selected payments
  const calculateSelectedPaymentsTotal = () => {
    return selectedPaymentsForInvoice.reduce((sum, payment) => sum + (payment.montantRegle || 0), 0);
  };

  const sourceReceipts = [...new Map((formik.values.lignes || [])
    .filter(line => line.sourceBonReceptionId)
    .map(line => [line.sourceBonReceptionId, line.sourceBonReceptionNumero])).entries()];
  const workflowSteps = [
    'Fournisseur et facture',
    'BR à facturer',
    'Lignes et quantités',
    'Retours, paiements et avances',
    'Vérification'
  ];


  return (
    <Container fluid>
      <Form onSubmit={formik.handleSubmit}>
        <Card className="mb-3 shadow-sm">
          <CardBody className="d-flex flex-wrap gap-2">
            {workflowSteps.map((label, index) => {
              const step = index + 1;
              return <Button key={label} type="button" size="sm" color={activeStep === step ? 'primary' : step < activeStep ? 'success' : 'light'} onClick={() => setActiveStep(step)}>
                {step}. {label}{step < activeStep ? ' ✓' : ''}
              </Button>;
            })}
          </CardBody>
        </Card>
        {/* <Row className="mb-3">
          <Col>
            {renderSelectPaymentsButton()}
          </Col>
        </Row> */}
        <Row>
          {/* Left Column - Invoice Details */}
          <Col md={8}>
            <Card className={activeStep === 1 ? '' : 'd-none'}>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <FaFileInvoice className="me-2" />
                  {isEditing ? t('facture.editInvoice') : t('facture.newInvoice')}
                  {isEditing && invoice?.numero && (
                    <Badge color="info" className="ms-2">
                      {invoice.numero}
                    </Badge>
                  )}
                </h5>
                <div>
                  {isEditing && invoice?.statut && (
                    <Badge
                      color={
                        invoice.statut === 'PAYEE' ? 'success' :
                          invoice.statut === 'ANNULEE' ? 'danger' :
                            invoice.statut === 'PARTIELLEMENT_PAYEE' ? 'warning' :
                              'secondary'
                      }
                      className="me-2"
                    >
                      {t(`facture.${invoice.statut.toLowerCase()}`)}
                    </Badge>
                  )}
                  {canBeUpdated === false && (
                    <Badge color="warning">
                      {t('facture.readOnly')}
                    </Badge>
                  )}
                  {formik.values.fromPayments && (
                    <Badge color="info">
                      {t('facture.fromPayments')}
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardBody>
                {formik.values.fromPayments && formik.values.paymentsRefs && (
                  <Alert color="success" className="mb-3">
                    <div className="fw-bold">{t('facture.fromPayments')}</div>
                    <div className="mt-1">Montant payé: {formatPrice(formik.values.montantTTC)} | Reste à payer: {formatPrice(0)}</div>
                    <div className="mt-2">
                      <span className="fw-bold">Référence du paiement origine de la facture:</span> {formik.values.paymentsRefs}
                    </div>
                  </Alert>
                )}

                {/* Display selected payments info if creating from payments */}
                {formik.values.fromPayments && selectedPaymentsForInvoice.length > 0 && (
                  <Card className="mb-3 border-info">
                    <CardHeader className="bg-info text-white">
                      <h6 className="mb-0 d-flex align-items-center">
                        <FaMoneyBillWave className="me-2" />
                        {t('facture.selectedPayments')}
                        <Badge color="light" className="ms-2 text-dark">
                          {selectedPaymentsForInvoice.length}
                        </Badge>
                      </h6>
                    </CardHeader>
                    <CardBody className="p-3">
                      <div className="table-responsive">
                        <Table size="sm" bordered>
                          <thead className="bg-light">
                            <tr>
                              <th>{t('payment.reference')}</th>
                              <th>{t('payment.date')}</th>
                              <th>{t('payment.mode')}</th>
                              <th>{t('payment.amount')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedPaymentsForInvoice.map(payment => (
                              <tr key={payment.id}>
                                <td>{payment.reference || '-'}</td>
                                <td>{payment.dateReglement}</td>
                                <td>{getPaymentModeLabel(payment.modeReglement)}</td>
                                <td className="text-end">{formatPrice(payment.montantRegle)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-light">
                            <tr>
                              <td colSpan="3" className="text-end fw-bold">{t('payment.total')}:</td>
                              <td className="text-end fw-bold">{formatPrice(calculateSelectedPaymentsTotal())}</td>
                            </tr>
                          </tfoot>
                        </Table>
                      </div>
                    </CardBody>
                  </Card>
                )}

                {/* Section des références de bons de livraison */}
                {isEditing && invoice?.referenceBons && (
                  <Card className="mb-3 border-info">
                    <CardHeader className="bg-info text-white">
                      <h6 className="mb-0 d-flex align-items-center">
                        <FaTruck className="me-2" />
                        {t('facture.deliveryReferences')}
                        <Badge color="light" className="ms-2 text-dark">
                          {parseBonLivraisonRefs(invoice.referenceBons).length}
                        </Badge>
                      </h6>
                    </CardHeader>
                    <CardBody className="p-3">
                      <div className="row">
                        {parseBonLivraisonRefs(invoice.referenceBons).map((ref, index) => (
                          <div key={index} className="col-md-6 col-lg-4 mb-2">
                            <div className="d-flex align-items-center p-2 bg-light rounded">
                              <FaTruck className="text-info me-2" size={14} />
                              <span className="fw-bold text-primary">{ref}</span>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Information supplémentaire */}
                      <div className="mt-3 pt-2 border-top">
                        <small className="text-muted d-flex align-items-center">
                          <FaInfoCircle className="me-1" />
                          {t('facture.deliveryReferencesNote')}
                        </small>
                      </div>
                    </CardBody>
                  </Card>
                )}
                <Row>
                  <Col md={3}>
                    <FormGroup>
                      <Label for="companyId">{t('company.company', 'Entreprise')}</Label>
                      <Input
                        type="select"
                        id="companyId"
                        name="companyId"
                        value={formik.values.companyId || ''}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={formik.touched.companyId && formik.errors.companyId}
                      >
                        <option value="">{t('company.selectCompany', 'Sélectionner une entreprise')}</option>
                        {companies?.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                      </Input>
                      {formik.touched.companyId && formik.errors.companyId && (
                        <div className="invalid-feedback">{formik.errors.companyId}</div>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label for="fournisseurNom">{t('facture.fournisseur.title')}</Label>
                      <Input
                        id="fournisseurNom"
                        name="fournisseurNom"
                        type="text"
                        value={formik.values.fournisseurNom || ''}
                        disabled
                      />
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label for="dateFacture">
                        <FaCalendarAlt className="me-1" />
                        {t('facture.dateFacture')} *
                      </Label>
                      <Input
                        type="date"
                        id="dateFacture"
                        name="dateFacture"
                        value={formik.values.dateFacture}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={formik.touched.dateFacture && formik.errors.dateFacture}
                      />
                      {formik.touched.dateFacture && formik.errors.dateFacture && (
                        <div className="invalid-feedback">{formik.errors.dateFacture}</div>
                      )}
                    </FormGroup>
                  </Col>
                   <Col md={3}>
                    <FormGroup>
                      <Label for="modeReglement">
                        <FaMoneyBillWave className="me-1" />
                        {t('facture.modeReglement')} *
                      </Label>
                      <Input
                        type="select"
                        id="modeReglement"
                        name="modeReglement"
                        value={formik.values.modeReglement}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={formik.touched.modeReglement && formik.errors.modeReglement}
                      >
                        <option value="">{t('common.select')}</option>
                        <option value="ESPECES">{t('facture.modeReglement.espece')}</option>
                        <option value="CHEQUE">{t('facture.modeReglement.cheque')}</option>
                        <option value="VIREMENT">{t('facture.modeReglement.virement')}</option>
                        <option value="CARTE_BANCAIRE">{t('facture.modeReglement.card')}</option>
                        <option value="TRAITE">{t('facture.modeReglement.traite')}</option>
                      </Input>
                      {formik.touched.modeReglement && formik.errors.modeReglement && (
                        <div className="invalid-feedback">{formik.errors.modeReglement}</div>
                      )}
                    </FormGroup>
                  </Col>
                </Row>

	                <Row>
		                  <Col md={6}>
		                    <FormGroup>
	                      <Label for="dateEcheance">
	                        <FaCalendarAlt className="me-1" />
	                        {t('facture.dateEcheance', 'Date échéance facture')}
                      </Label>
	                      <Input
	                        type="date"
	                        id="dateEcheance"
	                        name="dateEcheance"
	                        value={formik.values.dateEcheance}
	                        onChange={formik.handleChange}
	                        onBlur={formik.handleBlur}
	                        invalid={formik.touched.dateEcheance && formik.errors.dateEcheance}
	                      />
		                      {formik.touched.dateEcheance && formik.errors.dateEcheance && (
		                        <div className="invalid-feedback">{formik.errors.dateEcheance}</div>
		                      )}
		                    </FormGroup>
		                  </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="numeroFactfournisseur">
                            <FaFileInvoice className="me-1" />
                            {t('facture.numeroFactfournisseur', 'Numero facture fournisseur')}
                          </Label>
                          <Input
                            id="numeroFactfournisseur"
                            name="numeroFactfournisseur"
                            type="text"
                            value={formik.values.numeroFactfournisseur || ''}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                        </FormGroup>
                      </Col>
	                 
	                  {formik.values.modeReglement === 'CHEQUE' && (<>
	                    <Col md={3}>
                      <FormGroup>
                        <Label for="numeroCheque">{t('expense.numeroCheque')}</Label>
                        <Input
                          id="numeroCheque"
                          name="numeroCheque"
                          value={formik.values.numeroCheque || ''}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={formik.touched.numeroCheque && formik.errors.numeroCheque}
                        />
                        {formik.touched.numeroCheque && formik.errors.numeroCheque && (
                          <div className="invalid-feedback">{formik.errors.numeroCheque}</div>
                        )}
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label for="banque">{t('expense.banque')}</Label>
                        <Input
                          type="select"
                          id="banque"
                          name="banque"
                          value={formik.values.banque}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={formik.touched.banque && formik.errors.banque}
                        >
                          <option value="">{t('payment.selectBank', 'Sélectionner une banque')}</option>
                          <option value="BMCE">BMCE</option>
                          <option value="ATTIJARI">ATTIJARI</option>
                          <option value="CIH">CIH</option>
                          <option value="CREDIT DU MAROC">CREDIT DU MAROC</option>
                          <option value="SG">SG</option>
                        </Input>
                        {formik.touched.banque && formik.errors.banque && (
                          <div className="invalid-feedback">{formik.errors.banque}</div>
                        )}
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
	                      <Label for="dateEcheanceCheque">{t('facture.dateEcheanceCheque', 'Date échéance chèque')}</Label>
                        <Input
                          id="dateEcheanceCheque"
                          name="dateEcheanceCheque"
                          type="date"
	                          value={formik.values.dateEcheanceCheque || ''}
	                          onChange={formik.handleChange}
	                          onBlur={formik.handleBlur}
	                          invalid={formik.touched.dateEcheanceCheque && formik.errors.dateEcheanceCheque}
	                        />
                        {formik.touched.dateEcheanceCheque && formik.errors.dateEcheanceCheque && (
                          <div className="invalid-feedback">{formik.errors.dateEcheanceCheque}</div>
                        )}
                      </FormGroup>
                    </Col>

                  </>
                  )}
                </Row>


                {/* Supplier Information Display */}
                {formik.values.fournisseurId && (
                  <Card className="bg-light mb-3">
                    <CardBody className="p-3">
                      <div className="mb-0">
                        <small className="text-muted">{t('facture.fournisseur.title')}:</small>
                        <div>{formik.values.fournisseurNom || 'N/A'}</div>
                      </div>
                    </CardBody>
                  </Card>
                )}

                {/* Button to select payments if supplier is selected and not creating from payments already */}
                {formik.values.fournisseurId && !formik.values.fromPayments && !isEditing && (
                  <Button
                    color="info"
                    outline
                    className="mb-3"
                    onClick={() => {
                      loadClientPayments(formik.values.fournisseurId);
                      setShowSelectPaymentsModal(true);
                    }}
                  >
                    <FaMoneyBillWave className="me-2" />
                    {t('facture.selectPayments')}
                  </Button>
                )}

                {/* Observations */}
                <FormGroup>
                  <Label for="commentaire">
                    {t('facture.commentaire')}
                  </Label>
                  <Input
                    type="textarea"
                    id="commentaire"
                    name="commentaire"
                    rows="3"
                    value={formik.values.commentaire}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder={t('facture.commentairePlaceholder')}
                  />
                </FormGroup>
              </CardBody>
            </Card>

            {activeStep === 1 && <div className="d-flex justify-content-end my-3"><Button type="button" color="primary" onClick={() => setActiveStep(2)}>Continuer vers les BR</Button></div>}

            {activeStep === 2 && <Card className="shadow-sm mb-3">
              <CardHeader className="bg-primary text-white"><h5 className="mb-0">BR à facturer</h5></CardHeader>
              <CardBody>
                {isEditing && !canBeUpdated && <Alert color="warning">
                  Cette facture fournisseur est validée. La modification directe est interdite; utilisez l’annulation ou le workflow d’avoir fournisseur afin de préserver l’historique financier.
                </Alert>}
                <Alert color="info">Sélectionnez les BR à inclure. Seules les quantités encore facturables seront proposées.</Alert>
                {sourceReceipts.length === 0 ? <Alert color="warning">Aucun BR sélectionné.</Alert> : <div className="table-responsive"><Table size="sm" bordered><thead><tr><th>BR</th><th className="text-end">Lignes</th><th className="text-end">Montant proposé</th></tr></thead><tbody>{sourceReceipts.map(([id, reference]) => { const lines = formik.values.lignes.filter(line => Number(line.sourceBonReceptionId) === Number(id)); return <tr key={id}><td><strong>{reference}</strong></td><td className="text-end">{lines.length}</td><td className="text-end fw-bold">{formatPrice(lines.reduce((sum, line) => sum + Number(line.montantTTC || 0), 0))}</td></tr>; })}</tbody></Table></div>}
                <div className="d-flex justify-content-between"><Button type="button" outline onClick={() => setActiveStep(1)}>Précédent</Button><Button type="button" color="primary" disabled={sourceReceipts.length === 0} onClick={() => setActiveStep(3)}>Continuer vers les lignes</Button></div>
              </CardBody>
            </Card>}

            {/* Products Section */}
            <Card className={`shadow-sm ${activeStep === 3 ? '' : 'd-none'}`}>
              <CardHeader className="bg-secondary text-white d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <FaBarcode className="me-2" />
                  {t('facture.articles')}
                </h5>
                {/* <Button
                  type="button"
                  color="success"
                  size="sm"
                  onClick={() => setShowProductSearch(true)}
                >
                  <FaPlus className="me-1" />
                  {t('facture.addArticle')}
                </Button> */}
              </CardHeader>
              <CardBody>
                {formik.values.lignes.length === 0 ? (
                  <Alert color="info" className="text-center">
                    <FaBarcode size={48} className="mb-3 d-block mx-auto opacity-50" />
                    {t('facture.noArticles')}
                  </Alert>
                ) : (
                  <div className="table-responsive">
                    <Table striped bordered hover size="sm">
                      <thead className="bg-light">
                        <tr>
                          <th>BR source</th>
                          <th style={{ width: '100px' }}>{t('facture.code')}</th>
                          <th>{t('facture.designation')}</th>
                          <th>Reçue</th>
                          <th>Retournée</th>
                          <th>Déjà facturée</th>
                          <th>Facturable</th>
                          <th style={{ width: '80px' }}>{t('facture.quantite')}</th>
                          <th style={{ width: '80px' }}>{t('facture.unite')}</th>
                          <th style={{ width: '100px' }}>{t('facture.prixUnitaireTTC')}</th>
                          <th style={{ width: '80px' }}>{t('facture.tauxTVA')}</th>
                          <th style={{ width: '100px' }}>{t('facture.montantHT')}</th>
                          <th style={{ width: '100px' }}>{t('facture.montantTTC')}</th>
                          {/* <th style={{ width: '50px' }}></th> */}
                        </tr>
                      </thead>
                      <tbody>
                        {formik.values.lignes.map((ligne, index) => (
                          <tr key={index}>
                            <td><strong>{ligne.sourceBonReceptionNumero || 'Directe'}</strong></td>
                            <td>
                              <Input
                                type="text"
                                value={ligne.articleCode}
                                size="sm"
                                disabled
                              />
                            </td>
                            <td>
                              <Input
                                type="text"
                                value={ligne.articleNom}
                                size="sm"
                                disabled
                              />
                            </td>
                            <td>
                              {Number(ligne.quantiteRecue || ligne.quantite || 0)}
                            </td>
                            <td>{Number(ligne.quantiteRetournee || 0)}</td>
                            <td>{Number(ligne.quantiteDejaFacturee || 0)}</td>
                            <td className="fw-bold">{Number(ligne.quantiteRestanteFacturable ?? ligne.quantite ?? 0)}</td>
                            <td>
                              <Input
                                type="number"
                                value={ligne.quantite}
                                min="0"
                                max={ligne.quantiteRestanteFacturable ?? ligne.quantiteRecue ?? ligne.quantite}
                                step="0.01"
                                size="sm"
                                disabled={isEditing && !canBeUpdated}
                                onChange={event => updateInvoiceLineQuantity(index, event.target.value)}
                              />
                              {ligne.sourceBonReceptionNumero && <small className="text-muted d-block">
                                {Number(ligne.quantiteRecue || 0)} reçues − {Number(ligne.quantiteRetournee || 0)} retournées − {Number(ligne.quantiteDejaFacturee || 0)} déjà facturées
                              </small>}
                            </td>
                            <td>
                              <span className="small">{getUniteLabel(ligne.unite)}</span>
                            </td>
                            <td>
                              <Input
                                type="number"
                                value={ligne.prixUnitaireTTC}
                                min="0"
                                step="0.01"
                                size="sm"
                                disabled
                              />
                            </td>
                            <td>
                              <Input
                                type="number"
                                value={(ligne.tauxTVA * 100).toFixed(0)}
                                min="0"
                                max="100"
                                step="1"
                                size="sm"
                                disabled
                              />
                            </td>
                            <td className="text-end">
                              {formatPrice(ligne.montantHT, false)}
                            </td>
                            <td className="text-end">
                              {formatPrice(ligne.montantTTC, false)}
                            </td>
                            {/* <td>
                              <Button
                                type="button"
                                color="danger"
                                size="sm"
                                onClick={() => handleRemoveLine(index)}
                                title={t('facture.removeLine')}
                              >
                                <FaTrash size={12} />
                              </Button>
                            </td> */}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}

                {formik.touched.lignes && formik.errors.lignes && (
                  <Alert color="danger" className="mt-2">
                    {formik.errors.lignes}
                  </Alert>
                )}
              </CardBody>
            </Card>
            {activeStep === 3 && <div className="d-flex justify-content-between my-3"><Button type="button" outline onClick={() => setActiveStep(2)}>Précédent</Button><Button type="button" color="primary" disabled={!formik.values.lignes.some(line => Number(line.quantite || 0) > 0)} onClick={() => setActiveStep(4)}>Continuer vers les paiements</Button></div>}

            {activeStep === 4 && <Card className="shadow-sm mb-3">
              <CardHeader className="bg-primary text-white"><h5 className="mb-0">Retours, paiements et avances</h5></CardHeader>
              <CardBody>
                <Row className="g-3">
                  <Col md={4}><div className="border rounded p-3 h-100"><strong>Retours considérés</strong><div className="mt-2">{formatPrice(formik.values.lignes.reduce((sum, line) => sum + Number(line.quantiteRetournee || 0) * Number(line.prixUnitaireTTC || 0), 0))}</div><small className="text-muted">Déjà exclus des quantités facturables.</small></div></Col>
                  <Col md={4}><div className="border rounded p-3 h-100"><strong>Paiements associés aux BR</strong><div className="mt-2 text-success fw-bold">{formatPrice(Math.min(Number(formik.values.montantRegle || 0), Number(formik.values.montantTTC || 0)))}</div><small className="text-muted">Transfert proposé sans nouveau décaissement.</small></div></Col>
                  <Col md={4}><div className="border rounded p-3 h-100"><strong>Avances fournisseur</strong><div className="mt-2">Aucune avance non liée sélectionnée</div><small className="text-muted">Une avance indépendante exige une confirmation visible dans le règlement fournisseur.</small></div></Col>
                </Row>
                <div className="d-flex justify-content-between mt-3"><Button type="button" outline onClick={() => setActiveStep(3)}>Précédent</Button><Button type="button" color="primary" onClick={() => setActiveStep(5)}>Vérifier la facture</Button></div>
              </CardBody>
            </Card>}

            {activeStep === 5 && <Card className="shadow-sm mb-3"><CardHeader className="bg-success text-white"><h5 className="mb-0">Vérification et confirmation</h5></CardHeader><CardBody>
              <dl className="row mb-0"><dt className="col-sm-5">Fournisseur</dt><dd className="col-sm-7">{formik.values.fournisseurNom}</dd><dt className="col-sm-5">Référence fournisseur</dt><dd className="col-sm-7">{formik.values.numeroFactfournisseur || '-'}</dd><dt className="col-sm-5">BR sélectionnés</dt><dd className="col-sm-7">{sourceReceipts.map(([, ref]) => ref).join(', ')}</dd><dt className="col-sm-5">Total facture</dt><dd className="col-sm-7 fw-bold">{formatPrice(formik.values.montantTTC)}</dd><dt className="col-sm-5">Couverture BR proposée</dt><dd className="col-sm-7 text-success fw-bold">{formatPrice(Math.min(Number(formik.values.montantRegle || 0), Number(formik.values.montantTTC || 0)))}</dd><dt className="col-sm-5">Reste prévisionnel</dt><dd className="col-sm-7 text-danger fw-bold">{formatPrice(Math.max(0, Number(formik.values.montantTTC || 0) - Number(formik.values.montantRegle || 0)))}</dd></dl>
              <Alert color="warning" className="mt-3 mb-0">Un brouillon n’a aucun transfert permanent ni effet financier. La confirmation revalide les quantités et transfère atomiquement les affectations BR éligibles.</Alert>
              <Button type="button" outline className="mt-3" onClick={() => setActiveStep(4)}>Précédent</Button>
            </CardBody></Card>}
            {/* Payments Section - Only show for existing invoices */}
            {isEditing && invoice?.id && (
              <Card className="shadow-sm mt-4">
                <CardHeader className="bg-primary text-white d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <FaMoneyBillWave className="me-2" />
                    {t('payment.payments')}
                  </h5>

                </CardHeader>
                <CardBody>
                  {reglementsLoading ? (
                    <div className="text-center py-4">
                      <Spinner color="primary" />
                      <div className="mt-2 text-muted">{t('common.loading')}</div>
                    </div>
                  ) : invoicePayments.length === 0 ? (
                    <Alert color="info" className="text-center">
                      <FaMoneyBillWave size={48} className="mb-3 d-block mx-auto opacity-50" />
                      {t('payment.noPayments')}
                    </Alert>
                  ) : (
                    <div className="table-responsive">
                      <Table striped bordered hover size="sm">
                        <thead className="bg-light">
                          <tr>
                            <th>{t('reglement.dateReglement', 'Date règlement')}</th>
                            <th>{t('payment.mode')}</th>
                            <th>{t('payment.piece')}</th>
                            <th>{t('payment.amount')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoicePayments.map((payment) => (
                            <tr key={payment.id}>
                              <td>{payment.dateReglement ? new Date(payment.dateReglement).toLocaleDateString('fr-FR') : '-'}</td>
                              <td>
                                {payment.sourcePaiement === 'AVANCE'
                                  ? t('reglement.avance', 'Avance')
                                  : getPaymentModeLabel(payment.mode || payment.modeReglement)}
                              </td>
                              <td>{payment.referenceSourcePaiement || payment.numeroPiece || payment.referencePaiement || payment.reference || payment.refReglement || '-'}</td>
                              <td className="text-end fw-bold">{formatPrice(payment.montant ?? payment.montantRegle, false)}</td>

                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-light">
                          <tr>
                            <td colSpan="3" className="text-end fw-bold">{t('payment.totalPaid')}:</td>
                            <td className="text-end fw-bold">{formatPrice(calculateTotalPaid(), false)}</td>
                          </tr>
                          <tr>
                            <td colSpan="3" className="text-end fw-bold">{t('payment.remainingAmount')}:</td>

                            <td className="text-end">
                              <span className={`fw-bold ${calculateRemainingAmount() > 0 ? 'text-danger' : 'text-success'}`}>
                                {formatPrice(calculateRemainingAmount(), false)}
                              </span>
                            </td>
                          </tr>
                        </tfoot>
                      </Table>
                    </div>
                  )}
                </CardBody>
              </Card>
            )}
            {/* Payments from Reglements Section - Add this new section */}
            {stateFromPayments && paymentsForInvoice.length > 0 && (
              <Card className="shadow-sm mt-4">
                <CardHeader className="bg-primary text-white d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <FaMoneyBillWave className="me-2" />
                    {t('facture.paymentsFromReglements')}
                  </h5>
                  <Button
                    type="button"
                    color="light"
                    size="sm"
                    onClick={() => setShowPaymentsDetails(!showPaymentsDetails)}
                  >
                    {showPaymentsDetails ? t('common.hide') : t('common.show')}
                  </Button>
                </CardHeader>
                <CardBody>
                  <Alert color="info" className="mb-3">
                    <FaInfoCircle className="me-2" />
                    {t('facture.paymentsFromReglementsInfo')}
                  </Alert>

                  {showPaymentsDetails && (
                    <div className="table-responsive">
                      <Table striped bordered size="sm">
                        <thead>
                          <tr>
                            <th>{t('reglement.reference')}</th>
                            <th>{t('reglement.date')}</th>
                            <th>{t('reglement.modeReglement')}</th>
                            <th>{t('reglement.montant')}</th>
                            <th>{t('reglement.statut')}</th>
                            <th style={{ width: '50px' }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {paymentsForInvoice.map(payment => (
                            <tr key={payment.id}>
                              <td>{payment.reference || '-'}</td>
                              <td>{payment.dateReglement}</td>
                              <td>
                                <div className="d-flex align-items-center">
                                  <span className="me-2">{getPaymentModeIcon(payment.modeReglement)}</span>
                                  <span>{getPaymentModeLabel(payment.modeReglement)}</span>
                                </div>
                                {payment.modeReglement === 'CHEQUE' && payment.referencePaiement && (
                                  <small className="text-muted d-block">
                                    {payment.referencePaiement} - {payment.banque || ''}
                                  </small>
                                )}
                              </td>
                              <td className="text-end fw-bold">{formatPrice(payment.montantRegle, false)}</td>
                              <td>{getPaymentStatusBadge(payment.statut)}</td>
                              <td>
                                <Button
                                  type="button"
                                  color="danger"
                                  size="sm"
                                  onClick={() => handleRemovePayment(payment.id)}
                                  title={t('common.remove')}
                                >
                                  <FaTrash size={12} />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td colSpan="3" className="text-end fw-bold">{t('reglement.total')}:</td>
                            <td className="text-end fw-bold text-success">{formatPrice(calculatePaymentsTotal(), false)}</td>
                            <td colSpan="2"></td>
                          </tr>
                        </tfoot>
                      </Table>
                    </div>
                  )}
                </CardBody>
              </Card>
            )}
          </Col>

          {/* Right Column - Summary */}
          <Col md={4}>
            <Card className="shadow-sm sticky-top" style={{ top: '20px' }}>
              <CardHeader className="bg-info text-white">
                <h5 className="mb-0">
                  <FaCalculator className="me-2" />
                  {t('facture.summary')}
                </h5>
              </CardHeader>
              <CardBody>
                {/* Totals Summary */}
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-muted">{t('facture.totalHT')}:</span>
                    <span className="fw-bold">{formatPrice(formik.values.montantHT)}</span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-muted">{t('facture.totalTVA')}:</span>
                    <span className="fw-bold">{formatPrice(formik.values.montantTVA)}</span>
                  </div>
                  <hr />
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <span className="fw-bold text-primary">{t('facture.totalTTC')}:</span>
                    <span className="fw-bold text-primary fs-5">{formatPrice(formik.values.montantTTC)}</span>
                  </div>
                </div>

                {/* Payment Information */}
                {isEditing && invoice?.id && (
                  <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="text-muted">{t('payment.totalPaid')}:</span>
                      <span className="fw-bold text-success">{formatPrice(calculateTotalPaid())}</span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-muted">{t('payment.remainingAmount')}:</span>
                      <span className={`fw-bold ${calculateRemainingAmount() > 0 ? 'text-danger' : 'text-success'}`}>
                        {formatPrice(calculateRemainingAmount())}
                      </span>
                    </div>
                    {formik.values.fromPayments && formik.values.paymentsRefs && (
                      <div className="mt-2">
                        <span className="text-muted">Référence du paiement origine:</span>
                        <div className="fw-bold">{formik.values.paymentsRefs}</div>
                      </div>
                    )}
                  </div>
                )}

                {/* Payment Information for new invoices */}
                {!isEditing && !formik.values.fromPayments && (
                  <div className="mb-4">
                    <Alert color="info" className="small">
                      Les paiements déjà affectés aux BR seront transférés à la confirmation, sans créer un nouveau règlement.
                    </Alert>
                    <div className="d-flex justify-content-between"><span>Couverture BR estimée :</span><strong>{formatPrice(formik.values.montantRegle || 0)}</strong></div>

                    {/* Remaining Amount */}
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-muted">{t('facture.montantRestant')}:</span>
                      <span className={`fw-bold ${(formik.values.montantTTC - formik.values.montantRegle) > 0
                        ? 'text-danger'
                        : 'text-success'
                        }`}>
                        {formatPrice(formik.values.montantTTC - formik.values.montantRegle)}
                      </span>
                    </div>
                  </div>
                )}

                {/* Status */}
                {/* <div className="mb-4">
                  <FormGroup>
                    <Label for="statut">
                      {t('facture.status')}
                    </Label>
                    <Input
                      type="select"
                      id="statut"
                      name="statut"
                      value={formik.values.statut}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled={formik.values.fromPayments} // Disable if creating from payments
                    >
                      <option value="VALIDEE">{t('facture.validated')}</option>
                      <option value="IMPAYE">{t('facture.encours')}</option>
                      <option value="PAYEE">{t('facture.paid')}</option>
                      <option value="PARTIELLEMENT_PAYEE">{t('facture.partiallyPaid')}</option>
                      <option value="ANNULEE">{t('facture.cancelled')}</option>
                    </Input>
                  </FormGroup>
                </div> */}

                {/* Action Buttons */}
                {activeStep === 5 && <div className="d-grid gap-2">
                  {isEditing && invoice?.statut === 'IMPAYE' && (
                    <Button
                      color="danger"
                      outline
                      onClick={handleCancelInvoice}
                      disabled={invoiceLoading}
                    >
                      <FaTimes className="me-2" />
                      {t('facture.cancelInvoice')}
                    </Button>
                  )}

                  {!isEditing && <Button color="secondary" type="button" disabled={invoiceLoading} onClick={() => { formik.setFieldValue('statut', 'BROUILLON').then(() => formik.submitForm()); }}>
                    <FaSave className="me-2" />Enregistrer comme brouillon
                  </Button>}
                  {isEditing && invoice?.statut === 'BROUILLON' && <Button color="success" type="button" disabled={invoiceLoading} onClick={() => { formik.setFieldValue('statut', 'IMPAYE').then(() => formik.submitForm()); }}>
                    <FaCheckCircle className="me-2" />Créer et confirmer la facture fournisseur
                  </Button>}
                  <Button
                    color="primary"
                    type={isEditing ? 'submit' : 'button'}
                    onClick={!isEditing ? () => { formik.setFieldValue('statut', 'IMPAYE').then(() => formik.submitForm()); } : undefined}
                    disabled={invoiceLoading || invoice?.statut === 'ANNULEE' || !canBeUpdated}
                  >
                    {invoiceLoading ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        {isEditing ? t('common.updating') : t('common.saving')}
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        {isEditing ? invoice?.statut === 'BROUILLON' ? 'Enregistrer le brouillon' : t('common.editInvoice') : 'Créer et confirmer la facture fournisseur'}
                      </>
                    )}
                  </Button>
                </div>}

                {/* Invoice Statistics */}
                <div className="mt-4 pt-3 border-top">
                  <small className="text-muted d-block mb-2">{t('facture.statistics')}:</small>
                  <div className="d-flex justify-content-between">
                    <small className="text-muted">{t('facture.totalLines')}:</small>
                    <small className="fw-bold">{formik.values.lignes.length}</small>
                  </div>
                  <div className="d-flex justify-content-between">
                    <small className="text-muted">{t('facture.totalQuantity')}:</small>
                    <small className="fw-bold">
                      {formik.values.lignes.reduce((sum, ligne) => sum + (ligne.quantite || 0), 0).toFixed(2)}
                    </small>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Form>
    </Container>
  );
};

export default ViewFacture;



